import { useState } from 'react';
import {
  useShopifyMultipass,
  AllAddOnInfo,
} from '@viome/viome-base/src/usecase/Shop/ShopQuery';
import { useToast } from '@/hooks/use-toast';
import { EventTracker } from '@/utils';
import { analyticsEvent } from '@utils/analyticsEvent';

const withCartQuantity = (cartUrl: string, quantity: number) => {
  try {
    const url = new URL(cartUrl);
    url.searchParams.set('quantity', String(quantity));
    return url.toString();
  } catch {
    const withoutQuantity = cartUrl.replace(/([?&])quantity=\d+/gi, '');
    const separator = withoutQuantity.includes('?') ? '&' : '?';
    return `${withoutQuantity}${separator}quantity=${quantity}`;
  }
};

export const useAddOnOrderNow = () => {
  const { mutate: generateMultipassUrl, isPending: isMultipassLoading } =
    useShopifyMultipass();
  const { toast } = useToast();
  const [processingSku, setProcessingSku] = useState<string | null>(null);

  const handleOrderNow = (addOn: AllAddOnInfo, quantity = 1) => {
    EventTracker.trackEvent('shop', analyticsEvent.SHOP_ADDON_ORDERCTA);

    if (!addOn.cart_url) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Shopify cart URL not found.',
      });
      return;
    }

    setProcessingSku(addOn.sku);

    // Opened synchronously (not inside the async onSuccess) so Safari/iOS still
    // treats it as a user-gesture-triggered popup rather than blocking it.
    const checkoutTab = window.open('', '_blank');
    const cartUrl = withCartQuantity(addOn.cart_url, quantity);

    generateMultipassUrl(cartUrl, {
      onSuccess: (multipassData) => {
        setProcessingSku(null);
        if (multipassData) {
          if (checkoutTab && !checkoutTab.closed) {
            checkoutTab.location.href = multipassData;
          } else {
            window.location.href = multipassData;
          }
        } else {
          checkoutTab?.close();
          toast({
            variant: 'destructive',
            title: 'Error',
            description: 'Could not retrieve checkout URL. Please try again.',
          });
        }
      },
      onError: (error: Error) => {
        setProcessingSku(null);
        checkoutTab?.close();
        toast({
          variant: 'destructive',
          title: 'Error',
          description:
            error.message || 'Could not proceed to checkout. Please try again.',
        });
      },
    });
  };

  const isProcessing = (sku: string) =>
    isMultipassLoading && processingSku === sku;

  return { handleOrderNow, isProcessing };
};
